/**
 * Loads Partnero's universal tracking script (GROWTH-6931).
 *
 * Two callers need it, and nobody else loads the third-party script:
 * - the landing visit (`loadPartneroForReferral`, run at app start), so the script sets
 *   the referral cookie for a visitor who arrived through a partner link;
 * - the signup event (`ensurePartnero`, used by `getProfile`), for a flagged account whose
 *   page did not load the script, for example after an email-verification detour.
 */

export const PARTNERO_PROGRAM_ID = "TGSX7MKC";

// Partner links carry the partner key in the query string. Program TGSX7MKC reads `p` (the
// `query_param` of its Partnero settings), which is what our backend builds (PartneroService).
// Partnero's script reads only that parameter, so a `?ref=` or `?via=` landing loads it without
// setting the referral cookie. Both stay accepted so that a later change of the program's
// parameter cannot silently stop the landing load again, as the `?ref=` gate did in April 2026.
export const PARTNERO_REFERRAL_PARAMS = ["ref", "p", "via"];

const PARTNERO_SCRIPT_URL = "https://app.partnero.com/js/universal.js";
const PARTNERO_ASSETS_HOST = "https://assets.partnero.com";
// Partnero's snippet publishes the name of its queue on the window under this key.
const PARTNERO_OBJECT_KEY = "__partnerObject";

let loading = null;

export const hasPartneroReferralParam = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  return PARTNERO_REFERRAL_PARAMS.some(name => Boolean(params.get(name)));
};

// Same contract as Partnero's own snippet: `po(...)` calls made before universal.js has
// loaded are queued, and the script replays them once it runs.
const installQueue = () => {
  window[PARTNERO_OBJECT_KEY] = "po";

  function enqueue(...args) {
    const call = { a: args, q: [] };
    const pushed = this.push(call);
    return typeof pushed === "number" ? enqueue.bind(call.q) : pushed;
  }
  enqueue.q = [];

  window.po = enqueue.bind(enqueue.q);
  window.po.q = enqueue.q;
};

/**
 * Resolves once `window.po` is backed by the loaded Partnero script and rejects when the
 * script cannot be loaded (blocked, offline). One load per page session: later calls reuse
 * it, a failure included, so a blocked script is not requested again on every bootstrap.
 * A `window.po` supplied by someone else resolves at once and is left alone.
 */
export const ensurePartnero = () => {
  if (loading) {
    return loading;
  }
  if (typeof window.po === "function") {
    return Promise.resolve();
  }

  loading = new Promise((resolve, reject) => {
    installQueue();
    window.po("settings", "assets_host", PARTNERO_ASSETS_HOST);
    window.po("program", PARTNERO_PROGRAM_ID, "load");
    // Not `po("integration", "universal")`: that mode turns any submitted form with an email
    // into a Partnero customer. The dashboard has no signup form (registration happens on
    // Keycloak) and the signup event is sent explicitly, so it would only capture the forms of
    // an authenticated session, such as a teammate's email.

    const script = document.createElement("script");
    script.async = true;
    script.src = `${PARTNERO_SCRIPT_URL}?v=${Math.floor(Date.now() / 1e6)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Partnero script failed to load"));
    document.head.appendChild(script);
  });

  return loading;
};

/**
 * Landing visit: loads Partnero only when the URL carries a partner key. Never rejects;
 * resolves true when the script is loaded and false when it was skipped or blocked.
 */
export const loadPartneroForReferral = (search = window.location.search) => {
  if (!hasPartneroReferralParam(search)) {
    return Promise.resolve(false);
  }

  return ensurePartnero().then(
    () => true,
    () => false
  );
};
